import { NOTE_REQUEST_TIMEOUT_MS } from '@/services/driverNotesApi'
import type { LoadNotesSnapshot, NotePriorityUpdate } from '@numeo/types-core'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import {
    AddressLookupResult,
    ClientLaneLoad,
    LaneLoadInput,
    PayrollLockState,
    PodReportField,
    PodReportType,
    type RateconRevisionAnswer,
    ReportDelayParams,
} from '@numeo/types-main-web-app'
import {
    ClientEmail,
    ClientOngoingLoad,
    ClientRateConf,
    DeadheadConflictPreview,
    DeadheadOrigin,
    DeliveryStatusType,
    GeocodingSuggestion,
    InvoiceRateSyncOwner,
    LoadGroups,
    OngoingLoadCounts,
    OngoingLoadSettings,
    OngoingLoadStatus,
    PhotoType,
    RecommendationStatus,
    RecommendationSummary,
    SchedulerTruck,
    StopTypeFilter,
    Truck,
    TruckAssignmentRecommendation,
    UpdateLoadStatusError,
    UpdateLoadStatusSkipped,
    NotePriority,
} from '@numeo/types-core'
import { FuelDispatchStop } from '@numeo/types'
import type { TripDateBasis } from '@numeo/types-main-web-app'
import axios from 'axios'
import { TruckActiveLoad } from 'store/schedulerSlice'
import { isRecord } from 'utils/isRecord'
import { encodeOngoingLoadsListParams, OngoingLoadsListParams } from '../utils/ongoing-loads-list-params'

interface PaginatedResponse<T> {
    data: T[]
    meta?: {
        total: number
        page: number
        pageSize: number
    }
}

interface BatchAssignmentInput {
    loadId: string
    truckId: string
    skipTruckAvailabilityCheck?: boolean
    /** The dispatcher's "Yes" to the delivery-overdue confirm, sent on the retry for the flagged loads only. */
    confirmCompleteOverdueLoad?: boolean
    /** The dispatcher's "No" to that confirm: assign the truck anyway, without completing the load. */
    keepOverdueLoadUpcoming?: boolean
}

interface BatchAssignmentError {
    loadId: string
    error: string
    /** Set by the backend when the only thing blocking this load is an unanswered delivery-overdue confirm. */
    deliveryOverdue?: boolean
}

/** Named switches for {@link OngoingLoadsApi.assignTruck}. Every field is optional; omit what you don't set. */
interface AssignTruckOptions {
    mobilePreviewRevision?: string
    /** Dispatch message to send to the driver (compose-and-send flow only). */
    message?: string
    /** Simulated truck (development/testing). */
    isSimulated?: boolean
    /** Assign even though the truck is busy — the dispatcher's "Yes" to the truck-overlap confirm. */
    skipTruckAvailabilityCheck?: boolean
    /** Send the dispatch + fuel-stop messages after assigning. */
    dispatch?: boolean
    /** Clear a conflicting driver status — the dispatcher's "Yes" to the driver-status confirm. */
    overrideDriverStatus?: boolean
    /** Dispatcher-picked deadhead origin; null keeps the automatic pipeline. */
    deadheadOrigin?: DeadheadOrigin | null
    /**
     * Deadhead miles the dispatcher typed by hand, which wins over every computed source. Omit to
     * leave the automatic pipeline alone; `null` records "unknown" explicitly.
     */
    deadheadMilesOverride?: number | null
    /** Dispatcher-attached fuel stops, sent right after the dispatch message. */
    fuelStops?: FuelDispatchStop[]
    /** False leaves Auto Fuel on Dispatch out of this dispatch; omitted means included. */
    includeAutoFuelStops?: boolean
    /** The dispatcher's "Yes" to the delivery-overdue confirm: assign the truck and complete the load. */
    confirmCompleteOverdueLoad?: boolean
    /**
     * The dispatcher's "No" to it: assign the truck without completing the load. The load goes where the assignment
     * would take any other load, and the auto-archive leaves it alone until it completes.
     */
    keepOverdueLoadUpcoming?: boolean
    /** A dispatch of a load already in transit that leaves it there instead of taking it back to Dispatched. */
    keepInTransit?: boolean
}

interface DitatDocumentUrl {
    url: string
    revoke: () => void
}

const getMessageFromResponseData = (data: unknown): string | null => {
    if (typeof data === 'string' && data.trim()) return data
    if (isRecord(data) && typeof data.message === 'string' && data.message.trim()) return data.message
    return null
}

const getDitatDocumentErrorMessage = async (error: unknown): Promise<string> => {
    if (!axios.isAxiosError(error)) return error instanceof Error ? error.message : 'Failed to load DITAT document'

    const responseData = error.response?.data
    if (responseData instanceof Blob) {
        try {
            const message = getMessageFromResponseData(JSON.parse(await responseData.text()) as unknown)
            if (message) return message
        } catch {
            // The server may return a non-JSON error body; use Axios's message below.
        }
    }

    return getMessageFromResponseData(responseData) || error.message || 'Failed to load DITAT document'
}

interface LocateLoadResponse {
    found: boolean
    tab?: 'upcoming' | 'in-transit' | 'history'
    page?: number
    indexInPage?: number
}

interface UploadDeliveryDocumentsResponse {
    success: boolean
    documentTypeCounts?: Partial<Record<PhotoType, number>>
    /** The upload filed a POD on a load sitting at its delivery: offer to complete it now. */
    canCompleteNow?: boolean
}

export interface MergeDocumentsResponse {
    success: boolean
    data: {
        url: string
        /** Pages in the merged PDF — a source PDF contributes all of its own pages. */
        pageCount: number
        /** Present when the merge was filed as a photo proof. */
        sessionId?: string
        /** Rate confirmations only: the version it was filed as, and what the review made of it. */
        version?: number
        changedFields?: string[]
        revisionOpened?: boolean
        revisionSkippedReason?: 'invoiced'
        /** Set when the extracted con names a different load than the one it was filed on. */
        documentIdMismatch?: string
    }
    /** POD merges only: the load is sitting at its delivery with the paperwork now in hand. */
    canCompleteNow?: boolean
}

interface ProcessFileOptions {
    /** Only the general-agent chat sends it: a con whose load is already on the board opens that load's revision. */
    ifLoadExists?: 'revision'
}

interface ProcessFileResponse {
    success: boolean
    message: string
    load?: ClientOngoingLoad
    created?: boolean
    /** Present only when `ifLoadExists: 'revision'` was sent and the con's load was already on the board. */
    revision?: RateconRevisionAnswer
}

export type OngoingLoadDTO = Omit<ClientOngoingLoad, 'createdAt' | 'updatedAt' | 'id'>

export interface OngoingLoadsFinancialSummary {
    brokerRate: number
    assigneeRate: number
    reserveBalance: number
    completedTrips: number
}

interface OngoingLoadsListResponse {
    data: ClientOngoingLoad[]
    counts: OngoingLoadCounts
    pagination: { total: number; page: number; limit: number; pages: number }
    financialSummary?: OngoingLoadsFinancialSummary
    recommendationSummary?: RecommendationSummary
}

class OngoingLoadsApi {
    /**
     * Create a note for a load
     * @param loadId - The ID of the load to add a note to
     * @param text - The note text content
     * @param userId - The ID of the user creating the note
     * @returns The saved notes and their revision
     */
    async createNote(loadId: string, text: string, userId: string, priority?: NotePriority): Promise<LoadNotesSnapshot> {
        const response = await axios.post(`/updater/loads/${loadId}/note?response=notes`, {
            text,
            userId,
            ...(priority && { priority }),
        })
        return response.data
    }

    async updateNote(loadId: string, noteKey: string, text: string, priority?: NotePriorityUpdate): Promise<LoadNotesSnapshot> {
        const response = await axios.patch(`/updater/loads/${loadId}/note/${encodeURIComponent(noteKey)}?response=notes`, {
            text,
            ...(priority !== undefined && { priority }),
        })
        return response.data
    }

    async deleteNote(loadId: string, noteKey: string): Promise<LoadNotesSnapshot> {
        const response = await axios.delete(`/updater/loads/${loadId}/note/${encodeURIComponent(noteKey)}?response=notes`)
        return response.data
    }

    /**
     * Get all loads with pagination
     * Returns loads with all statuses (ACTIVE, IN_TRANSIT, COMPLETED, CANCELLED)
     * Server-side pagination and filtering is used based on status, page, and limit
     */
    async getOngoingLoads(
        status: string,
        page: number = 1,
        limit: number = 10,
        signal?: AbortSignal,
        // Accounting "Ready to Invoice" tab: ask the server for the ready subset + a server-side search,
        // so a ready load older than the fetched window still appears (and search spans all pages).
        // `deliveredFrom`/`deliveredTo` (`YYYY-MM-DD`, inclusive) are that tab's invoicing-period filter —
        // they narrow to loads DELIVERED in the period, which is the date the tab already shows and ages on.
        options?: { readyToInvoice?: boolean; search?: string; deliveredFrom?: string; deliveredTo?: string }
    ): Promise<{
        data: ClientOngoingLoad[]
        counts: OngoingLoadCounts
        pagination: { total: number; page: number; limit: number; pages: number }
        financialSummary?: OngoingLoadsFinancialSummary
        recommendationSummary?: RecommendationSummary
    }> {
        const response = await axios.get('/updater/loads', {
            params: {
                status,
                page,
                limit,
                ...(options?.readyToInvoice ? { readyToInvoice: 'true' } : {}),
                ...(options?.search ? { search: options.search } : {}),
                ...(options?.deliveredFrom ? { deliveredFrom: options.deliveredFrom } : {}),
                ...(options?.deliveredTo ? { deliveredTo: options.deliveredTo } : {}),
            },
            signal,
        })
        return {
            data: response.data.ongoingLoads,
            counts: response.data.counts,
            pagination: response.data.pagination,
            financialSummary: response.data.financialSummary,
            recommendationSummary: response.data.recommendationSummary,
        }
    }

    /**
     * Create a new ongoing load
     */
    async createOngoingLoad(load: OngoingLoadDTO, truckId?: string): Promise<ClientOngoingLoad> {
        const response = await axios.post('/updater/loads', load, { params: { truckId } })
        return response.data
    }

    /**
     * Get an existing ongoing load
     */
    async getOngoingLoadById(id: string): Promise<ClientOngoingLoad> {
        const response = await axios.get(`/updater/loads/${id}`)
        return response.data
    }

    /** Read the projected notes snapshot used to recover a socket revision gap. */
    async getLoadNotesBatch(ids: string[], signal: AbortSignal): Promise<LoadNotesSnapshot[]> {
        const response = await axios.post<LoadNotesSnapshot[]>(
            '/updater/loads/notes/batch/v1',
            { ids },
            {
                baseURL: getApiBaseUrlWithoutV1(),
                signal,
                timeout: NOTE_REQUEST_TIMEOUT_MS,
            }
        )
        return response.data
    }

    async getLoadNotes(loadId: string, signal: AbortSignal): Promise<LoadNotesSnapshot> {
        const { data } = await axios.get<LoadNotesSnapshot>(`/updater/loads/notes/${encodeURIComponent(loadId)}/v1`, {
            baseURL: getApiBaseUrlWithoutV1(),
            signal,
            timeout: NOTE_REQUEST_TIMEOUT_MS,
        })
        return data
    }

    /**
     * Update an existing ongoing load.
     *
     * readyBy takes null to clear it, not undefined: JSON.stringify drops an undefined value, so the
     * request body arrives as {} and the controller's `'readyBy' in req.body` guard never fires.
     */
    async updateOngoingLoad(
        id: string,
        // `invoiceRateSyncOwner` is transport-only, not a load field: it names who writes
        // load_invoice_data in this same save, so the automatic rate sync — and, for the rate
        // endpoint, its Timeline entry — stands down instead of racing and double-logging.
        load: Omit<Partial<OngoingLoadDTO>, 'readyBy'> & { readyBy?: Date | null; invoiceRateSyncOwner?: InvoiceRateSyncOwner }
    ): Promise<ClientOngoingLoad> {
        const response = await axios.put(`/updater/loads/${id}`, load)
        return response.data
    }

    /**
     * Dispatch a load that already has its truck: it moves to Dispatched and the driver gets the message
     * (and fuel stops). Nothing about the assignment is redone. A new-convention route, so it targets the
     * host root rather than the global `/v1` base.
     */
    async dispatchLoad(
        loadId: string,
        options: {
            mobilePreviewRevision?: string
            expectedStatus?: OngoingLoadStatus
            /** For a load in transit: message the driver and leave it on the road. */
            keepInTransit?: boolean
            message?: string
            fuelStops?: FuelDispatchStop[]
            includeAutoFuelStops?: boolean
        }
    ): Promise<{ success: boolean; data: ClientOngoingLoad; messageSent: boolean }> {
        const response = await axios.post(
            '/updater/dispatch/loads/v1',
            {
                loadId,
                ...(options.expectedStatus ? { expectedStatus: options.expectedStatus } : {}),
                ...(options.mobilePreviewRevision ? { mobilePreviewRevision: options.mobilePreviewRevision } : {}),
                ...(options.keepInTransit ? { keepInTransit: true } : {}),
                ...(options.message ? { message: options.message } : {}),
                ...(options.fuelStops?.length ? { fuelStops: options.fuelStops } : {}),
                ...(options.includeAutoFuelStops === false ? { includeAutoFuelStops: false } : {}),
            },
            { baseURL: getApiBaseUrlWithoutV1() }
        )
        return response.data
    }

    /**
     * Which of this load's fields its locked/settled payroll statements freeze.
     *
     * A new-convention route (`/loads/payroll-lock/:loadId/v1`), so it targets the host root rather than
     * the global `/v1` base. Contract-aware on the server: whether the driver rate is editable depends on
     * the contract each settled trip was priced with, which the client cannot see.
     */
    async getPayrollLockState(loadId: string): Promise<PayrollLockState> {
        const response = await axios.get<{ success: boolean; data: PayrollLockState }>(`/loads/payroll-lock/${loadId}/v1`, {
            baseURL: getApiBaseUrlWithoutV1(),
        })
        return response.data.data
    }

    /**
     * Delete multiple ongoing loads
     */
    async deleteOngoingLoads(ids: string[]): Promise<void> {
        await axios.delete('/updater/loads/multiple', {
            data: {
                ids,
            },
        })
    }

    /**
     * Get loads by document ID or broker name
     * @param searchTerm - The search term (document ID or broker name)
     * @param status - Array of statuses to filter by
     * @param dispatcherIds - Optional dispatcher IDs to filter by (string or array)
     * @param loadGroupId - Optional load group ID to filter by
     * @param sort - Optional sort parameter (supports delay, delivery status, pickup, and delivery sorting)
     * @param page - Page number for pagination (default: 1)
     * @param limit - Number of items per page (default: 10)
     * @returns The matching ongoing loads, or empty array if not found
     */
    async getByDocumentId(
        searchTerm: string,
        status: string[],
        dispatcherIds?: string[] | string,
        loadGroupId?: string,
        sort?: string,
        page: number = 1,
        limit: number = 10,
        brokers?: string[],
        brokerFilterMode?: 'filter' | 'priority',
        laneFilterMode?: 'all' | 'only' | 'exclude',
        attentionNeeded?: boolean,
        unassignedRiskThresholds?: number[],
        startDate?: string,
        endDate?: string,
        driverIds?: string[],
        truck?: string,
        stopTypeFilter?: StopTypeFilter[],
        tagIds?: string[],
        payrollBatchIds?: string[],
        includePayrollPlacement?: boolean,
        dateBasis?: TripDateBasis
    ): Promise<OngoingLoadsListResponse> {
        return this.listLoads({
            searchTerm,
            status,
            dispatcherIds,
            loadGroupId,
            sort,
            page,
            limit,
            brokers,
            brokerFilterMode,
            laneFilterMode,
            attentionNeeded,
            unassignedRiskThresholds,
            startDate,
            endDate,
            driverIds,
            truck,
            stopTypeFilter,
            tagIds,
            payrollBatchIds,
            includePayrollPlacement,
            dateBasis,
        })
    }

    /**
     * The Ongoing Loads list by named filters — what `getByDocumentId` sends, without its positional arguments, so a new
     * filter can't silently land in another filter's slot. Prefer it for new callers.
     */
    async listLoads({ page = 1, limit = 10, ...filters }: OngoingLoadsListParams & { page?: number; limit?: number }): Promise<OngoingLoadsListResponse> {
        const params = encodeOngoingLoadsListParams(filters)
        if (page) params.append('page', page.toString())
        if (limit) params.append('limit', limit.toString())
        const response = await axios.get(`/updater/loads/by-rate-conf-id?`, { params: params })
        return {
            data: response.data.ongoingLoads,
            counts: response.data.counts,
            pagination: response.data.pagination,
            financialSummary: response.data.financialSummary,
            recommendationSummary: response.data.recommendationSummary,
        }
    }

    /**
     * Locate a load in the paginated list based on current filters/sort
     */
    async locateLoad(
        params: {
            loadId: string
            searchTerm?: string
            dispatcherIds?: string[]
            loadGroupId?: string
            sort?: string
            pageSize?: number
            brokers?: string[]
            brokerFilterMode?: 'filter' | 'priority'
            laneFilterMode?: 'all' | 'only' | 'exclude'
        },
        signal?: AbortSignal
    ): Promise<LocateLoadResponse> {
        const response = await axios.post('/updater/loads/locate', params, { signal })
        return response.data
    }

    /**
     * Upload file for processing
     * @param file - The file to upload
     * @param documentId - Optional document ID to check for duplicates
     * @param options - `ifLoadExists: 'revision'` opens a revision on a load already carrying the con, instead of updating it
     * @returns The response data including success status, message, and processed loads if available
     */
    async processFile(file: File, documentId?: string, options?: ProcessFileOptions): Promise<ProcessFileResponse> {
        // Create a FormData object for multipart/form-data upload
        const formData = new FormData()

        // Append the file with field name 'file' to match multer configuration
        formData.append('file', file)

        // Add documentId as a form field if provided
        if (documentId) {
            formData.append('documentId', documentId)
        }

        if (options?.ifLoadExists) {
            formData.append('ifLoadExists', options.ifLoadExists)
        }

        const url = '/updater/loads/process'

        const response = await axios.post(
            url,
            formData, // Send as multipart/form-data
            {
                headers: {
                    // Content-Type is automatically set by the browser with the correct boundary
                    // when using FormData, so we don't need to set it manually
                },
            }
        )
        return response.data
    }

    /**
     * Update load status
     * @param ids Array of objects containing load IDs
     * @param status New status to set for all loads
     * @param tonuRate Optional TONU rate to set (only applicable when updating a single load)
     * @returns Object with updated loads, failed load IDs, and errors
     * @throws {Error} When trying to update TONU rate for multiple loads
     */
    async updateLoadStatus(
        ids: string[],
        status: OngoingLoadStatus,
        tonuRate?: number,
        forceEarlierLoad?: boolean
    ): Promise<{
        success: boolean
        updatedLoads: ClientOngoingLoad[]
        errors: UpdateLoadStatusError[]
        skipped: UpdateLoadStatusSkipped[]
    }> {
        try {
            if (tonuRate !== undefined && ids.length !== 1) {
                throw new Error('TONU rate can only be updated for one load at a time')
            }
            const payload = {
                ids,
                status,
                ...(tonuRate !== undefined && { tonuRate }),
                ...(forceEarlierLoad && { forceEarlierLoad }),
            }

            const response = await axios.put('/updater/loads/status', payload)

            // Log errors for failed loads
            if (response.data.errors && response.data.errors.length > 0) {
                response.data.errors.forEach((err: { loadId: string; error: string }) => {
                    console.error(`Failed to update load status for load ${err.loadId}:`, err.error)
                })
            }

            return {
                success: response.data.success || false,
                updatedLoads: response.data.updatedLoads || [],
                errors: response.data.errors || [],
                skipped: response.data.skipped || [],
            }
        } catch (error: unknown) {
            console.error('Error updating load status:', error)

            // Handle different error types
            let errorMessage = 'Failed to update load status'

            if (axios.isAxiosError(error)) {
                // Handle Axios errors
                errorMessage = error.response?.data?.message || error.message || errorMessage

                // Log errors for failed loads if available
                if (error.response?.data?.errors) {
                    error.response.data.errors.forEach((err: { loadId: string; error: string }) => {
                        console.error(`Failed to update load status for load ${err.loadId}:`, err.error)
                    })
                }
            } else if (error instanceof Error) {
                // Handle standard Error objects
                errorMessage = error.message
            }

            throw new Error(errorMessage)
        }
    }

    /**
     * Update delivery status
     * @param loadIds - Array of load IDs to update
     * @param status - The new delivery status type
     * @param stopIndex - Optional stop index to use for the status update
     * @returns The updated loads if available
     */
    async updateDeliveryStatus(
        loadIds: string[],
        status: DeliveryStatusType,
        stopIndex?: number
    ): Promise<{
        success: boolean
        updatedLoads: ClientOngoingLoad[]
        errors: Array<{ loadId: string; error: string }>
    }> {
        try {
            // Update the delivery status with a single request
            const response = await axios.put('/updater/loads/delivery-status', { loadId: loadIds, status, stopIndex })

            // If the response includes the updated load, add it to our array
            return {
                success: response.data.success,
                updatedLoads: response.data.updatedLoads,
                errors: response.data.errors,
            }
        } catch (error) {
            console.error(`Error updating delivery status for loads ${loadIds}:`, error)
            return {
                success: false,
                updatedLoads: [],
                errors: [],
            }
        }
    }

    /**
     * Assign both a truck and dispatcher to a load.
     *
     * Everything past (loadId, truck) is a named option deliberately: this used to be ten positional
     * parameters, so a caller that only wanted the last one had to pad with `undefined, undefined, …` and a
     * single miscount would silently send e.g. `fuelStops: true`. Add new switches here, not to the arg list.
     *
     * @param loadId - The ID of the load to assign
     * @param truck - The truck to assign
     * @param options - Assignment switches; see AssignTruckOptions
     * @returns The result of the assignment operation, including any stop recommendation metadata
     */
    async assignTruck(
        loadId: string,
        truck: Truck,
        options: AssignTruckOptions = {}
    ): Promise<{ success: boolean; data: ClientOngoingLoad; recommendation?: TruckAssignmentRecommendation }> {
        const {
            message,
            isSimulated,
            skipTruckAvailabilityCheck,
            dispatch,
            mobilePreviewRevision,
            overrideDriverStatus,
            deadheadOrigin,
            deadheadMilesOverride,
            fuelStops,
            includeAutoFuelStops,
            confirmCompleteOverdueLoad,
            keepOverdueLoadUpcoming,
            keepInTransit,
        } = options
        const response = await axios.post(`/updater/loads/assign`, {
            loadId,
            truck,
            message,
            isSimulated,
            skipTruckAvailabilityCheck,
            overrideDriverStatus,
            dispatch,
            mobilePreviewRevision,
            // Send the dispatcher's picked origin as-is (incl. GPS) — the backend persists it only on a
            // deliberate assign; a default assign has deadheadOrigin = null and stays on the auto pipeline.
            ...(deadheadOrigin ? { deadheadOrigin } : {}),
            // Sent only when the dispatcher actually typed a mileage — an absent key is what tells the
            // backend to keep deriving it, and `null` is a meaningful value here (deadhead unknown),
            // so this cannot collapse into a truthiness check.
            ...(deadheadMilesOverride !== undefined ? { deadheadMilesOverride } : {}),
            ...(fuelStops?.length ? { fuelStops } : {}),
            ...(includeAutoFuelStops === false ? { includeAutoFuelStops: false } : {}),
            ...(confirmCompleteOverdueLoad ? { confirmCompleteOverdueLoad } : {}),
            ...(keepOverdueLoadUpcoming ? { keepOverdueLoadUpcoming } : {}),
            ...(keepInTransit ? { keepInTransit } : {}),
        })
        return response.data
    }

    async unassignTruck(loadId: string, truckId: string): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.post(`/updater/loads/unassign`, { loadId, truckId })
        return response.data
    }

    async assignTrailer(loadId: string, trailerId: string): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.post(`/updater/loads/assign-trailer`, { loadId, trailerId })
        return response.data
    }

    async unassignTrailer(loadId: string, trailerId: string): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.post(`/updater/loads/unassign-trailer`, { loadId, trailerId })
        return response.data
    }

    /**
     * Split an in-transit load at a route stop, replacing the outgoing driver.
     */
    async splitLoad(
        loadId: string,
        payload: {
            stopIndex: number
            splitAt: string
            outgoingDriverGross?: number
            outgoingDriverMiles?: number
            incomingDriverGross?: number
            incomingDriverMiles?: number
            autoCalculated: boolean
            incomingTruckId?: string
            incomingDriverId?: string
        }
    ): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.post(`/updater/loads/${loadId}/split`, payload)
        return response.data
    }

    /**
     * Replace the recovery driver on an already-split load.
     */
    async replaceRecoveryDriver(loadId: string, payload: { newDriverId: string; newTruckId: string }): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.put(`/updater/loads/${loadId}/recovery-driver`, payload)
        return response.data
    }

    /**
     * Update the outgoing driver's split segment details (gross / miles) on an in-transit split load.
     */
    async updateSplitDetails(
        loadId: string,
        payload: { outgoingDriverGross?: number; outgoingDriverMiles?: number; incomingDriverGross?: number; incomingDriverMiles?: number }
    ): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.patch(`/updater/loads/${loadId}/split-details`, payload)
        return response.data
    }

    /**
     * Revert a split on an in-transit load, removing all recovery drivers
     * and restoring the load to its pre-split state.
     */
    async revertSplit(loadId: string): Promise<{ success: boolean; data: ClientOngoingLoad }> {
        const response = await axios.delete(`/updater/loads/${loadId}/split`)
        return response.data
    }

    /**
     * Send email to recipient
     * @param formData - Form data containing email details
     * @returns Thread ID or error object
     */
    async sendEmail(formData: FormData): Promise<string | null | { error: string; metadata?: { canStartNewThread?: boolean } }> {
        try {
            const response = await axios.post('/updater/loads/send-email', formData)
            return response.data.threadId || null
        } catch (error) {
            // Normalize Axios error so we surface backend's NumeoRestError.message
            let message = 'Failed to send email'
            let metadata: { canStartNewThread?: boolean } = {}

            if (axios.isAxiosError(error)) {
                message = error.response?.data?.message || error.message || message
                metadata = error.response?.data?.metadata
            } else if (error instanceof Error) {
                message = error.message
            }

            return { error: message, metadata }
        }
    }

    /**
     * Send message to internal team
     * @param loadId - The ID of the load
     * @param subject - Email subject
     * @param htmlBody - HTML email body
     * @returns Success status or error object
     */
    async sendInternalTeamMessage(loadId: string, subject: string, htmlBody: string): Promise<{ success: boolean } | { error: string }> {
        try {
            const response = await axios.post('/updater/loads/send-internal-group-message', {
                loadId,
                subject,
                htmlBody,
            })
            return response.data
        } catch (error) {
            return { error: (error as Error).message }
        }
    }
    /**
     * Get rate confirmations for a project
     * @param projectName - The project name
     * @param page - Page number (0-based)
     * @param pageSize - Number of items per page
     * @param documentId - Optional document ID to search for
     * @param startDate - Optional start date for filtering by date range
     * @param endDate - Optional end date for filtering by date range
     */
    async getRateCons(
        projectName: string,
        page: number = 0,
        byInterval: boolean,
        pageSize: number = 10,
        documentId?: string,
        startDate?: string,
        endDate?: string
    ): Promise<PaginatedResponse<ClientRateConf>> {
        // We'll send the dates directly to the API
        // The backend will handle parsing them

        const response = await axios.get('/updater/loads/rate-cons', {
            params: {
                projectName,
                page,
                pageSize,
                documentId,
                startDate,
                endDate,
                byInterval,
            },
        })
        // Return the whole response to preserve metadata
        return response.data
    }

    /**
     * Import selected rate confirmations to create ongoing loads
     * @returns The response including created loads if available
     */
    async importRateCons(rateConIds: string[], projectName: string): Promise<{ success: boolean; loads?: ClientOngoingLoad[] }> {
        const response = await axios.post('/updater/loads/import-rate-cons', {
            rateConIds,
            projectName,
        })
        return response.data || { success: true }
    }

    /**
     * Manually calculate ETA for a load with optional buffer
     * @param loadId - The ID of the load to calculate ETA for
     * @param truck - The truck object with truck details
     * @returns The updated load with new ETA data
     */
    async calculateETA(loadId: string, truck: Truck, bufferMinutes?: number): Promise<ClientOngoingLoad> {
        const response = await axios.post('/updater/loads/eta', { loadId, truck, bufferMinutes })
        return response.data
    }

    /**
     * Update ETA buffer for a load
     * @param loadId - The ID of the load to update buffer for
     * @param bufferMinutes - Buffer time in minutes (undefined to remove buffer)
     * @returns Response with success status and updated load
     */
    async updateETABuffer(
        loadId: string,
        bufferMinutes?: number
    ): Promise<{
        success: boolean
        load: ClientOngoingLoad
        message: string
    }> {
        const response = await axios.put('/updater/loads/eta-buffer', { loadId, bufferMinutes })
        return response.data
    }

    /**
     * Manually calculate ETA for multiple loads
     * @param loadIds - Array of load IDs to calculate ETA for
     * @returns Object with results for each load and summary
     */
    async calculateBulkETA(loadIds: string[]): Promise<{
        success: boolean
        results: Array<{
            loadId: string
            success: boolean
            error?: string
            load?: ClientOngoingLoad
        }>
        summary: {
            total: number
            successful: number
            failed: number
        }
    }> {
        const response = await axios.post('/updater/loads/bulk-eta', { loadIds })
        return response.data
    }

    async acceptPendingBOL(loadId: string, reasonBOL: string, stopIndex?: number): Promise<{ success: boolean; load?: ClientOngoingLoad }> {
        try {
            const body: { loadId: string; reasonBOL: string; stopIndex?: number } = { loadId, reasonBOL }
            if (stopIndex !== undefined) body.stopIndex = stopIndex
            const response = await axios.post('/updater/loads/accept-pending-bol', body)
            return { success: true, load: response.data }
        } catch (error) {
            console.error('Error accepting BOL:', error)
            throw error
        }
    }

    async acceptPendingPOD(loadId: string, reasonPOD?: string): Promise<{ success: boolean; load?: ClientOngoingLoad }> {
        try {
            const response = await axios.post('/updater/loads/accept-pending-pod', { loadId, reasonPOD })
            return { success: true, load: response.data }
        } catch (error) {
            console.error('Error accepting POD:', error)
            throw error
        }
    }

    /**
     * Report POD verification issue for tracking AI agent accuracy
     * @param loadId - The ID of the load
     * @param reportType - Type of report (false_positive, false_negative, incorrect_data, other)
     * @param affectedFields - Fields affected by the issue
     * @param notes - Optional notes
     * @returns Success status
     */
    async reportPodIssue(loadId: string, reportType: PodReportType, affectedFields: PodReportField[], notes?: string): Promise<{ success: boolean }> {
        try {
            const response = await axios.post('/updater/loads/report-pod-issue', {
                loadId,
                reportType,
                affectedFields,
                notes,
            })
            return { success: response.data.success }
        } catch (error) {
            console.error('Error reporting POD issue:', error)
            throw error
        }
    }

    async reportDelay(params: Omit<ReportDelayParams, 'userId'>) {
        const response = await axios.post('/updater/loads/report-delay', params)
        return response.data
    }

    async getTruckStatus(truckId: string, returnAddress?: boolean) {
        const response = await axios.get(`/trucks/location/${truckId}`, {
            params: {
                returnAddress,
            },
        })
        return response.data
    }

    async updateNotificationSettings(params: OngoingLoadSettings & { loadId: string }) {
        const response = await axios.post('/updater/loads/update-notification-settings', params)
        return response.data
    }

    /**
     * Upload multiple delivery documents for a load
     * @param loadId - The ID of the load to attach documents to
     * @param files - Array of files to upload
     * @returns The response with success status
     */
    async uploadDeliveryDocuments(loadId: string, files: File[]): Promise<UploadDeliveryDocumentsResponse> {
        // Create a FormData object for multipart/form-data upload
        const formData = new FormData()

        // Append loadId to the form data
        formData.append('loadId', loadId)

        // Append each file to the form data
        files.forEach((file) => {
            formData.append('files', file)
        })

        const response = await axios.post('/updater/loads/doc/upload', formData)
        return response.data
    }

    /**
     * Merge freshly picked files into one PDF and file it under `type`, skipping AI classification.
     * @param files - Sources in page order; the server merges them in exactly this order.
     * @param options - `revisionBaseline: 'originalRate'` reviews a rate con's revision against the broker-facing rate (the chat)
     */
    async mergeUploadDocuments(
        loadId: string,
        files: File[],
        type: PhotoType,
        rotations: number[] = [],
        options?: { revisionBaseline?: 'originalRate' }
    ): Promise<MergeDocumentsResponse> {
        const formData = new FormData()
        formData.append('loadId', loadId)
        formData.append('type', type)
        if (options?.revisionBaseline) {
            formData.append('revisionBaseline', options.revisionBaseline)
        }
        // Multipart carries no arrays, so the per-page rotations travel as JSON alongside the files.
        formData.append('rotations', JSON.stringify(rotations))
        files.forEach((file) => {
            formData.append('files', file)
        })

        const response = await axios.post('/updater/loads/doc/merge-upload', formData)
        return response.data
    }

    /**
     * Merge documents already on the load into one PDF. The sources are replaced by the result.
     * @param urls - Source document URLs in page order.
     */
    async mergeExistingDocuments(loadId: string, urls: string[], type: PhotoType, rotations: number[] = []): Promise<MergeDocumentsResponse> {
        const response = await axios.post('/updater/loads/doc/merge-existing', { loadId, urls, type, rotations })
        return response.data
    }

    /**
     * Delete delivery documents for a load
     * @param loadId - The ID of the load to delete the documents from
     * @param files - Array of file URLs to delete
     * @returns The response with success status and deleted count
     */
    async deleteDeliveryDocuments(loadId: string, files: string[]): Promise<{ success: boolean; message?: string }> {
        const response = await axios.delete('/updater/loads/doc/delete', {
            data: { loadId, files },
            headers: {
                'Content-Type': 'application/json',
            },
        })
        return response.data
    }

    /**
     * Fetches and returns emails for a specific load's email thread,
     * and resets the unread count for the load's email history.
     *
     * @param {string} loadId - The ID of the load
     * @returns {Promise<ClientEmail[]>} Array of email objects
     * @throws {NumeoRestError} When load is not found, missing thread ID, or other errors
     */
    async readEmails(loadId: string, threadId?: string): Promise<ClientEmail[]> {
        const response = await axios.get(`/updater/loads/${loadId}/emails`, {
            params: { threadId },
        })
        return response.data
    }

    /**
     * Sends updates to broker
     * @param loadIds - Array of load IDs to send updates to
     * @param notificationType - Type of notification to send ('email' or 'telegram')
     * @returns The response with success status
     */
    async sendUpdatesToBroker(loadIds: string[], notificationType: 'email' | 'telegram') {
        const response = await axios.post('/updater/loads/send-updates', { loadIds, notificationType })
        return response.data
    }

    /**
     * Adds loads to a load group
     * @param loadIds - Array of load IDs to add to the group
     * @param loadGroupId - ID of the load group to add the loads to
     * @param removeFromGroup - Whether to remove loads from their current group before adding
     * @returns The response with success status
     */
    async addLoadsToLoadGroup(loadIds: string[], loadGroupId?: string, removeFromGroup?: boolean) {
        const response = await axios.post('/updater/loads/add-to-group', { loadIds, loadGroupId, removeFromGroup })
        return response.data
    }

    /**
     *
     * @param projectName
     * @param groupId
     * @returns
     */
    async getLoadGroup(projectName: string, groupId: string) {
        const response = await axios.get(`/updater/settings/${projectName}/load-groups/${groupId}`)
        return response.data
    }

    /**
     *
     * @param projectName
     * @param loadGroupId
     * @returns
     */
    async deleteLoadGroup(projectName: string, loadGroupId: string) {
        const response = await axios.delete(`/updater/settings/${projectName}/delete-group`, { data: { loadGroupId } })
        return response.data
    }

    /**
     *
     * @param projectName
     * @param updatedLoadGroup
     * @returns
     */

    async updateLoadGroup(projectName: string, updatedLoadGroup: LoadGroups) {
        const response = await axios.put(`/updater/settings/${projectName}/update-group`, updatedLoadGroup)
        return response.data
    }

    async getLaneLoads(projectName: string): Promise<ClientLaneLoad[]> {
        const response = await axios.get(`/updater/settings/${projectName}/lane-loads`)
        return response.data?.lanes || []
    }

    async createLaneLoad(projectName: string, lane: LaneLoadInput): Promise<ClientLaneLoad> {
        const response = await axios.post(`/updater/settings/${projectName}/lane-loads`, lane)
        return response.data?.lane
    }

    async updateLaneLoad(projectName: string, laneId: string, lane: LaneLoadInput): Promise<ClientLaneLoad> {
        const response = await axios.put(`/updater/settings/${projectName}/lane-loads/${laneId}`, lane)
        return response.data?.lane
    }

    async deleteLaneLoad(projectName: string, laneId: string): Promise<void> {
        await axios.delete(`/updater/settings/${projectName}/lane-loads/${laneId}`)
    }

    /**
     * Get rate confirmations for a project by date range
     * @param projectName - The project name
     * @param startDate - The start date for the date range
     * @param endDate - The end date for the date range
     * @returns The rate confirmations for the project within the specified date range
     */
    async getUnassignedLoads(startDate: string, endDate?: string, skip?: number, limit?: number, signal?: AbortSignal): Promise<ClientOngoingLoad[]> {
        const response = await axios.get(`/updater/loads/unassigned-loads`, { params: { startDate, endDate, skip, limit }, signal })
        return response.data
    }

    /**
     * Get assigned loads for a project by date range
     * @param startDate - The start date for the date range
     * @param endDate - The end date for the date range
     * @returns The assigned loads for the project within the specified date range
     */
    async getAssignedLoads(startDate: string, endDate: string, skip?: number, limit?: number, signal?: AbortSignal): Promise<ClientOngoingLoad[]> {
        const response = await axios.get(`/updater/loads/assigned-loads`, { params: { startDate, endDate, skip, limit }, signal })
        return response.data
    }

    /**
     * Get suggestions for a load
     * @param loadId - The load ID
     * @returns The suggestions for the load
     */
    async getSuggestions(loadId: string): Promise<SchedulerTruck[]> {
        const response = await axios.get(`/updater/loads/suggested-trucks`, { params: { loadId } })
        return response.data
    }

    async getTrucksActiveLoads(truckIds: string[], signal?: AbortSignal): Promise<TruckActiveLoad[]> {
        const response = await axios.post(`/updater/loads/trucks/active-loads`, { truckIds }, { signal })
        return response.data
    }

    /**
     * Fetch the loads the Smart Suggestions banner acts on for a tab — on demand, when the user expands
     * the banner. Page open only carries recommendationSummary (counts); this returns the actual loads.
     */
    async getRecommendations(status: RecommendationStatus, signal?: AbortSignal): Promise<ClientOngoingLoad[]> {
        const response = await axios.get('/updater/loads/recommendations', { params: { status }, signal })
        return response.data.loadsWithRecommendations ?? []
    }

    /**
     * Dismiss all recommendations the banner shows for a tab in one server-side call (no ids sent, no
     * docs returned) — replaces the per-id dismiss that froze the page on large sets.
     */
    async dismissAllRecommendations(status: RecommendationStatus): Promise<{ dismissedCount: number }> {
        const response = await axios.put('/updater/loads/recommendations/dismiss', null, { params: { status } })
        return { dismissedCount: response.data.dismissedCount ?? 0 }
    }

    /**
     * Batch assign trucks to multiple loads in a single request.
     */
    async batchAssignTrucks(assignments: BatchAssignmentInput[]): Promise<{
        success: boolean
        updatedLoads: ClientOngoingLoad[]
        errors: BatchAssignmentError[]
    }> {
        const response = await axios.post('/updater/loads/batch-assign', { assignments })
        return response.data
    }

    async applyGlobalSettings(loadIds: string[]): Promise<{ success: boolean; updatedLoads: ClientOngoingLoad[] }> {
        const response = await axios.post('/updater/loads/apply-global-settings', { loadIds })
        return response.data
    }

    async getDitatSignedDocumentUrl(projectName: string, integrationId: string, rawUrl: string): Promise<DitatDocumentUrl | null> {
        try {
            const response = await axios.get(`/integrations/ditat/document-file`, {
                params: { projectName, integrationId, rawUrl },
                responseType: 'blob',
            })
            const url = URL.createObjectURL(response.data)
            let revoked = false
            return {
                url,
                revoke: () => {
                    if (revoked) return
                    revoked = true
                    URL.revokeObjectURL(url)
                },
            }
        } catch (error) {
            const message = await getDitatDocumentErrorMessage(error)
            console.error('Error getting Ditat document:', message)
            throw new Error(message)
        }
    }

    async reportProblemOnRatecons(rateconDocIds: string[], message: string): Promise<{ success: boolean; message?: string }> {
        const response = await axios.post('/updater/loads/report-problem', { rateconDocIds, message })
        return response.data
    }

    /**
     * Get formatted email body template for a load
     * @param loadId - The ID of the load
     * @returns The formatted email body string
     */
    async getFormattedEmailBody(loadId: string): Promise<string> {
        const response = await axios.get(`/updater/loads/formatted-email-body/${loadId}`)
        return response.data.emailBody || ''
    }

    async searchAddress(address: string): Promise<GeocodingSuggestion[]> {
        const response = await axios.post('/updater/loads/search-address', { address })
        return response.data
    }

    // Resolves a free-typed street address into its parsed City/State/ZIP components, for the load-stop
    // form's auto-populate feature.
    async lookupAddress(address: string): Promise<AddressLookupResult> {
        const response = await axios.post('/updater/loads/lookup-address', { address })
        return response.data
    }

    /**
     * Resolves the deadhead origin to pre-fill in the "Enter address" picker when assigning a load
     * to a truck: the active chain anchor delivery (in-transit / prebooked prior load), else the
     * truck's most recent completed delivery. Returns null if none (picker stays empty → GPS).
     */
    async getDeadheadOriginLocation(
        truckId: string,
        loadId: string
    ): Promise<{ address: string; coordinates: { lat: number; lng: number }; loadNumber?: string } | null> {
        const response = await axios.get(`/updater/loads/trucks/${truckId}/deadhead-origin`, { params: { loadId } })
        return response.data?.data ?? null
    }

    /**
     * Read-only preview of whether assigning `loadId` to `truckId` right now would produce a
     * `hasDeadheadTimeConflict` warning — some other active load on the truck won't even be
     * delivered until after this load's own delivery (an outright double-booking). Nothing is
     * written; purely informational. `anchorLoadNumber` names the load the deadhead chains from
     * (shown whether or not there's a conflict); `conflictLoadNumber` names the load actually
     * causing the conflict, which can be a *different* load than the anchor.
     */
    async previewDeadheadConflict(truckId: string, loadId: string): Promise<DeadheadConflictPreview> {
        const response = await axios.get(`/updater/loads/trucks/${truckId}/deadhead-conflict`, { params: { loadId } })
        return response.data?.data ?? { hasDeadheadTimeConflict: false }
    }

    /**
     * Computes a load's deadhead miles from a chosen origin (truck GPS / map point / address) without
     * assigning or dispatching. `mode: 'preview'` (default) only RETURNS the miles and writes nothing to
     * the load; `mode: 'save'` persists the computed miles to `driverDeadheadMiles`. Returns the
     * deadhead miles either way.
     */
    async applyDeadheadOrigin(loadId: string, deadheadOrigin: DeadheadOrigin, truckId?: string, mode: 'save' | 'preview' = 'preview'): Promise<number | null> {
        const response = await axios.post(`/updater/loads/${loadId}/deadhead-origin`, { deadheadOrigin, truckId, mode })
        return response.data?.data?.driverDeadheadMiles ?? null
    }

    async confirmGeocoding(loadId: string, stopIndex?: number): Promise<ClientOngoingLoad> {
        const response = await axios.post(`/updater/loads/${loadId}/geocoding/confirm`, { stopIndex })
        return response.data
    }

    async updateLoadAddress(loadId: string, suggestion: GeocodingSuggestion, stopIndex?: number): Promise<ClientOngoingLoad> {
        const response = await axios.put(`/updater/loads/${loadId}/address`, { suggestion, stopIndex })
        return response.data
    }

    /**
     * Send tracking reminder (recipients are determined server-side by user Task Settings)
     * @param loadId - The ID of the load
     * @returns Success status, timestamp, and notification details
     */
    async sendTrackingReminder(
        loadId: string,
        options?: {
            taskId?: string
            messageOverride?: string
        }
    ): Promise<{
        success: boolean
        trackingReminderSentAt: string
        sentToDriver: boolean
        sentToInternalTeam: boolean
        sentToDispatcher: boolean
        driverTelegramMissing: boolean
        dispatcherTelegramMissing: boolean
        internalTeamTelegramMissing?: boolean
        failureDetail?: string
        debugInfo?: {
            dispatchersFound: number
            dispatchers: Array<{
                id: string
                name?: string
                hasTelegram: boolean
                chatId?: string
                error?: string
            }>
            truckId?: string
            loadDispatcherId?: string
        }
    }> {
        const response = await axios.post(`/updater/loads/${loadId}/tracking-reminder`, options)
        return response.data
    }

    /**
     * @deprecated Use sendTrackingReminder instead
     */
    async sendTrackingReminderToDriver(loadId: string) {
        return this.sendTrackingReminder(loadId)
    }

    /**
     * Update the type of a photo proof
     * @param loadId - The ID of the load
     * @param photoUrl - The URL of the photo to update
     * @param newType - The new photo type
     * @returns The updated load, plus `canCompleteNow` when filing it as a POD leaves the load ready
     *          to close — the same prompt the upload endpoints raise.
     */
    async updatePhotoType(loadId: string, photoUrl: string, newType: string): Promise<ClientOngoingLoad & { canCompleteNow?: boolean }> {
        const response = await axios.put(`/updater/loads/${loadId}/photo-type`, {
            photoUrl,
            newType,
        })
        return response.data
    }

    /**
     * Point a load at a broker email thread.
     *
     * Its own endpoint rather than a field on updateOngoingLoad: the thread moves on its own between
     * saves (a reply the provider refuses to thread comes back on a new conversation), so the load
     * save drops the field rather than let a stale copy from the sidebar put the old thread back.
     */
    async attachBrokerEmailThread(loadId: string, threadId: string): Promise<ClientOngoingLoad> {
        const response = await axios.put(`/updater/loads/${loadId}/broker-email-thread`, { threadId })
        return response.data
    }
}

export const ongoingLoadsApi = new OngoingLoadsApi()
